// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

const routes: (RouteRecordRaw & {
    meta?: {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;
    };
})[] = [
    {
        name: "home",
        path: "/",
        component: () => import("@/components/routes/HomePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "template-wizard",
        path: "/template-wizard",
        component: () => import("@/components/routes/TemplateWizardPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "templates",
        path: "/templates",
        component: () => import("@/components/routes/TemplatesListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "template-detail",
        path: "/templates/:id",
        component: () => import("@/components/routes/TemplateDetailPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "projects",
        path: "/projects",
        component: () => import("@/components/routes/ProjectsListPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "project-new",
        path: "/project-new",
        component: () => import("@/components/routes/ProjectNewPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "project-edit",
        path: "/project-edit",
        component: () => import("@/components/routes/ProjectNewPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "project-detail",
        path: "/projects/:id",
        component: () => import("@/components/routes/ProjectDetailPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "report-new",
        path: "/projects/:projectId/report-new",
        component: () => import("@/components/routes/ReportNewPage.vue"),
        props: (route) => ({ projectId: route.params.projectId, templateId: route.query.templateId }),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "report-detail",
        path: "/projects/:projectId/reports/:reportId",
        component: () => import("@/components/routes/projects/ReportDetailPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "report-preview",
        path: "/projects/:projectId/reports/:reportId/preview",
        component: () => import("@/components/routes/projects/ReportPreviewPage.vue"),
        props: true,
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "qgis",
        path: "/qgis",
        component: () => import("@/components/routes/QgisTablesPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "dataspace",
        path: "/dataspace",
        component: () => import("@/components/routes/dataspace/FairDashboardPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "custom-query",
        path: "/custom-query",
        component: () => import("@/components/routes/CustomQueryPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "geo-upload",
        path: "/geo-upload",
        component: () => import("@/components/routes/GeoUploadPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "rich-text-editor",
        path: "/rich-text-editor",
        component: () => import("@/components/routes/RichTextEditorPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "about",
        path: "/about",
        component: () => import("@/components/routes/AboutPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "terms",
        path: "/terms",
        component: () => import("@/components/routes/TermsOfUsePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "cookies",
        path: "/cookies",
        component: () => import("@/components/routes/CookiePolicyPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "privacy",
        path: "/privacy",
        component: () => import("@/components/routes/PrivacyPolicyPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    /* Auth */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
    },

    {
        name: "tfa-login",
        path: "/login/tfa",
        component: () => import("@/components/routes/auth/TwoFactorLoginPage.vue"),
    },

    {
        name: "signup",
        path: "/signup",
        component: () => import("@/components/routes/auth/SignupPage.vue"),
    },
    {
        name: "signup-success",
        path: "/signup/success",
        component: () => import("@/components/routes/auth/SignupSuccessPage.vue"),
    },

    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
    },

    {
        name: "tp-login",
        path: "/login/tp/:service",
        component: () => import("@/components/routes/auth/ThirdPartyLogin.vue"),
    },

    {
        name: "tp-signup",
        path: "/signup/tp",
        component: () => import("@/components/routes/auth/ThirdPartySignupPage.vue"),
    },

    /* User Configuration */

    {
        name: "account-settings",
        path: "/settings",
        component: () => import("@/components/routes/account/SettingsPage.vue"),
    },

    /* Default */

    {
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
    },
];

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
